import React from 'react';
import { Button, Icon, PresenceStack } from '../../components';
import { useIdentity } from '../../hooks/useIdentity';
import usePresence from '../../hooks/usePresence';
import './DesktopHeader.css';

const noop = (label) => () => {
  // eslint-disable-next-line no-console
  console.log(`open ${label} panel`);
};

/**
 * DesktopHeader — 60px chat-region header at ≥ 900px.
 *
 * Layout: [room icon] [room name + presence sub] [presence stack] [Members button]
 *
 * Reads real presence via `usePresence` (F4.2). Both the stack and the
 * Members button are wired to `onMembersClick` so either click target opens
 * the members panel.
 */
const DesktopHeader = ({
  presenceText,
  onMembersClick,
  sidebarCollapsed,
  onExpandSidebar,
}) => {
  const { partyCode, isInGlob } = useIdentity();
  const { members, count } = usePresence();

  const code = isInGlob ? 'GLOB' : partyCode;
  const icon = isInGlob ? 'globe' : 'lock';

  // Default sub-line: live online count when we have one, otherwise the
  // design's placeholder copy.
  const sub =
    presenceText
      || (count > 0
        ? `${count} online · ${isInGlob ? 'the global room' : 'private party'}`
        : isInGlob
          ? 'the global room'
          : 'private party');

  const membersHandler = onMembersClick || noop('members');

  return (
    <header className="dheader" role="banner">
      {sidebarCollapsed && onExpandSidebar && (
        <button
          type="button"
          className="dheader__expand"
          onClick={onExpandSidebar}
          aria-label="Open sidebar"
          title="Open sidebar"
        >
          <Icon name="menu" size={16} aria-hidden />
        </button>
      )}
      <span className="dheader__icon" aria-hidden="true">
        <Icon name={icon} size={18} />
      </span>
      <div className="dheader__title">
        <span className={`dheader__name${isInGlob ? '' : ' dheader__name--code'}`}>
          {isInGlob ? '#GLOB' : code}
        </span>
        <span className="dheader__sub">
          <span className="dheader__sub-dot" aria-hidden="true" />
          {sub}
        </span>
      </div>
      <div className="dheader__right">
        <PresenceStack members={members} max={5} onClick={membersHandler} />
        <Button variant="ghost" onClick={membersHandler} aria-label="Open members panel">
          <Icon name="users" size={14} />
          Members
        </Button>
      </div>
    </header>
  );
};

export default DesktopHeader;
